import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectTaxProfileStatus } from '../selectors/tax.selector';
import { TAX_PROFILE_STATUS } from '../types/payout.types';
import { TabValue } from '../constants/prize';

interface TabContextType {
    currentTab: TabValue;
    setCurrentTab: (tab: TabValue) => void;
}

const TabContext = createContext<TabContextType | undefined>(undefined);

export const TabProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentTab, setCurrentTab] = React.useState<TabValue>(TabValue.PRIZES);
    const location = useLocation();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');

        if (tabParam) {
            const tabMapping: Record<string, TabValue> = {
                tax: TabValue.TAX,
                prizes: TabValue.PRIZES
            };

            const mappedTab = tabMapping[tabParam];
            if (mappedTab) {
                setCurrentTab(mappedTab);
            }
        }
    }, [location.search]);

    return (
        <TabContext.Provider value={{ currentTab, setCurrentTab }}>{children}</TabContext.Provider>
    );
};

export const useTab = (): TabContextType => {
    const context = useContext(TabContext);
    if (!context) {
        throw new Error('useTab must be used within a TabProvider');
    }
    return context;
};

export const useTabLabel = (): { invalidTaxLabel?: boolean; invalidPrizeLabel?: boolean } => {
    const taxProfileStatus = useSelector(selectTaxProfileStatus);
    const requiresUpdateStatuses: TAX_PROFILE_STATUS[] = [
        TAX_PROFILE_STATUS.NAME_MISS_MATCH,
        TAX_PROFILE_STATUS.INVALID_TAX_INFO,
        TAX_PROFILE_STATUS.MISSING_TAX_INFO,
        TAX_PROFILE_STATUS.DEACTIVATED,
        TAX_PROFILE_STATUS.TAX_INFO_REQUIRES_UPDATE,
        TAX_PROFILE_STATUS.NULL
    ];
    return {
        invalidTaxLabel: Boolean(
            taxProfileStatus &&
                requiresUpdateStatuses.includes(taxProfileStatus as TAX_PROFILE_STATUS)
        )
    };
};
